export type NotificationCapabilities = {
  adminRead: boolean
  adminManage: boolean
  providerRead: boolean
  providerManage: boolean
  globalScope: boolean
}

export type NotificationTabKey =
  | 'inbox'
  | 'history'
  | 'preferences'
  | 'health'
  | 'deliveries'
  | 'templates'
  | 'categories'
  | 'rules'
  | 'subscriptions'
  | 'providers'

export type NotificationTab = {
  key: NotificationTabKey
  label: string
}

const PERSONAL_TABS: NotificationTab[] = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'history', label: 'History' },
  { key: 'preferences', label: 'Preferences' }
]

const ADMIN_TABS: NotificationTab[] = [
  { key: 'health', label: 'Health' },
  { key: 'deliveries', label: 'Deliveries' },
  { key: 'templates', label: 'Templates' },
  // Categories has its own tab. It used to sit stacked ABOVE Rules inside the
  // Rules tab, so the page a reader opened for rules was headed "Categories" —
  // two first-class concepts sharing one tab, with the wrong one on top.
  { key: 'categories', label: 'Categories' },
  { key: 'rules', label: 'Rules' },
  { key: 'subscriptions', label: 'Subscriptions' }
]

/**
 * Which set of screens a surface shows.
 *
 * `personal` is what every user has: their own inbox, history and preferences.
 * `management` is estate configuration for a handful of admins. They were one
 * nine-tab strip, so most users carried six tabs they could never use, and the
 * two audiences shared a page.
 */
export type NotificationSurface = 'personal' | 'management'

export function buildNotificationTabs(
  capabilities: NotificationCapabilities,
  surface: NotificationSurface = 'personal'
): NotificationTab[] {
  if (surface === 'personal') {
    return PERSONAL_TABS
  }
  return [
    ...(capabilities.adminRead ? ADMIN_TABS : []),
    ...(capabilities.providerRead
      ? [{ key: 'providers' as const, label: 'Providers' }]
      : [])
  ]
}

export function canSelectNotificationScope(
  capabilities: Pick<NotificationCapabilities, 'globalScope'>
): boolean {
  return capabilities.globalScope
}

export function bindNotificationScope<T extends Record<string, unknown>>(
  value: T,
  scope: { type: string; code: string },
  field: 'entity' | 'scope'
): T {
  return { ...value, [field]: scope }
}

export function formatNotificationTime(
  value?: string | null,
  locale?: string
): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString(locale)
}
